import type { ApiFilter, ApiFilterOperator } from '@/store/table/table'
import {
  normalizeTableFilterValue,
  parseTableDate,
} from '@/utils/tableFilterValue'

/** What a cell holds: the raw value, and the name the Gateway gave to a code. */
export type CellView = { raw: unknown; name?: string }

export type TextValue = { contains?: string; picks?: string[] }
export type ListValue = string[]
export type NumberValue = { min?: number; max?: number }
/** Days as YYYY-MM-DD. `to` is the last day shown. */
export type DateValue = { from?: string; to?: string }

export type FilterCodec<V> = {
  toApi: (field: string, value: V) => ApiFilter[]
  /** The value in the entries of one field, or null when they hold no filter of this kind. */
  fromApi: (filters: ApiFilter[]) => V | null
  test: (cell: CellView, value: V) => boolean
}

const entry = (name: string, value: string, operator: ApiFilterOperator): ApiFilter => ({
  name,
  value: [value],
  operator,
})

const GTE = 'GREATER_THAN_OR_EQUAL_TO'
const LTE = 'LESS_THAN_OR_EQUAL_TO'
const LT = 'LESS_THAN'

const toNumber = (raw: unknown): number => {
  const text = String(raw ?? '').replace(/,/g, '').trim()
  return text === '' ? NaN : Number(text)
}

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const textCodec: FilterCodec<TextValue> = {
  toApi: (field, { contains, picks }) => {
    if (contains) return [{ name: field, value: [contains], operator: 'CONTAINS' }]
    if (picks?.length) return [{ name: field, value: picks }]
    return []
  },
  fromApi: filters => {
    const first = filters.find(f => f.operator === 'CONTAINS' && f.value[0])
    if (first) return { contains: first.value[0] }
    const picks = filters.find(f => !f.operator && f.value.length > 0)
    return picks ? { picks: picks.value } : null
  },
  test: ({ raw, name }, { contains, picks }) => {
    const value = normalizeTableFilterValue(raw)
    if (contains) {
      const wanted = normalizeTableFilterValue(contains)
      return value.includes(wanted) || normalizeTableFilterValue(name).includes(wanted)
    }
    const wanted = (picks ?? []).map(normalizeTableFilterValue)
    return wanted.length === 0 || wanted.some(item => value === item || value.includes(item))
  },
}

export const listCodec: FilterCodec<ListValue> = {
  toApi: (field, ids) => (ids.length > 0 ? [{ name: field, value: ids }] : []),
  fromApi: filters => {
    const picks = filters.find(f => !f.operator && f.value.length > 0)
    return picks ? picks.value : null
  },
  test: ({ raw }, ids) => {
    const value = normalizeTableFilterValue(raw)
    return ids.map(normalizeTableFilterValue).includes(value)
  },
}

export const numberCodec: FilterCodec<NumberValue> = {
  toApi: (field, { min, max }) => [
    ...(finite(min) ? [entry(field, String(min), GTE)] : []),
    ...(finite(max) ? [entry(field, String(max), LTE)] : []),
  ],
  fromApi: filters => {
    const out: NumberValue = {}
    for (const { value, operator } of filters) {
      const n = toNumber(value[0])
      if (!Number.isFinite(n)) continue
      if (operator === GTE) out.min = n
      else if (operator === LTE) out.max = n
      else if (operator === 'EQUALS' || !operator) {
        out.min = n
        out.max = n
      }
    }
    return finite(out.min) || finite(out.max) ? out : null
  },
  test: ({ raw }, { min, max }) => {
    const n = toNumber(raw)
    if (!Number.isFinite(n)) return false
    return (!finite(min) || n >= min) && (!finite(max) || n <= max)
  },
}

const DAY_MS = 86_400_000

const parseDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

const formatDay = (ms: number) => new Date(ms).toISOString().slice(0, 10)

const DAY = /^\d{4}-\d{2}-\d{2}$/

export const dateCodec: FilterCodec<DateValue> = {
  toApi: (field, { from, to }) => [
    ...(from ? [entry(field, from, GTE)] : []),
    ...(to ? [entry(field, formatDay(parseDay(to) + DAY_MS), LT)] : []),
  ],
  fromApi: filters => {
    const out: DateValue = {}
    for (const { value, operator } of filters) {
      const day = value[0]
      if (!day || !DAY.test(day)) continue
      if (operator === GTE) out.from = day
      else if (operator === LT) out.to = formatDay(parseDay(day) - DAY_MS)
      else if (!operator) {
        out.from = day
        out.to = day
      }
    }
    return out.from || out.to ? out : null
  },
  test: ({ raw }, { from, to }) => {
    const day = parseTableDate(raw)
    if (day === null) return false
    return (!from || day >= parseDay(from)) && (!to || day < parseDay(to) + DAY_MS)
  },
}
